import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {HomePageService} from '../../services/home-page.service';
import {NewsSectionLayout} from '@lib-modules/section/components/news-section/news-section.component';
import {MatchSectionLayout} from '@lib-modules/section/components/match-section/match-section.component';
import {FilterSectionLayout} from '@lib-modules/section/components/filter-section/filter-section.component';
import {LayoutService} from '@utils/services/layout/layout.service';
import { ColorScheme } from '@lib/models/color-scheme/color-scheme';
import { AppPromotionService } from '@app/core/services/promotion/app-promotion.service';
import { UserService } from '@app/core/services/user.service';
import {ShopService} from '@modules/shop/services/shop.service';
import { SportType } from '@lib/models/sport-type/sport-type';
import {NewsContentType} from '@lib-modules/cards/services/news-card/news-card.service';
import {SectionObserverService} from '@lib-modules/section/services/section-observer.service';
import {PickerItem} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {StateService} from '@core/services/state.service';
import {LoginType} from '@api/models/user/user';
import {EntityType} from '@lib/models/entity/entity-type';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['../../../../../../shared/styles/home.scss', './home-page.component.scss'],
})
export class HomePageComponent implements OnInit, OnDestroy {
  protected readonly EntityType = EntityType;
  public loading = false;
  private routeSubscription: Subscription;
  public newsMixedLayout = NewsSectionLayout.Mixed;
  public newsRowLayout = NewsSectionLayout.Row;
  public newsRowThumbnailLayout = NewsSectionLayout.RowThumbnail;
  public newsColumnLayout = NewsSectionLayout.Column;
  public matchesMinimalLayout = MatchSectionLayout.Minimal;
  public filterSectionLayout = FilterSectionLayout.Overview;
  public newsType = NewsContentType.EntityNews;
  public colorScheme = ColorScheme.Dark;
  public handballType = SportType.Handball;
  public stateSectionIntercepted = false;

  constructor (
    private route: ActivatedRoute,
    private router: Router,
    public homePageService: HomePageService,
    public layoutService: LayoutService,
    public promoService: AppPromotionService,
    public userService: UserService,
    public shopService: ShopService,
    private sectionObserver: SectionObserverService,
    private cdr: ChangeDetectorRef,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.loading = true;
    this.layoutService.setShowScrollUp(false);
    await this.promoService.getRegistrationFunnel();
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.onPathChange(params.get('state'));
    });
  }

  public ngOnDestroy() {
    this.sectionObserver.disconnect();
    this.homePageService.selectedState = null;
    this.layoutService.setShowScrollUp(true);
    this.routeSubscription?.unsubscribe();
  }

  private async onPathChange (stateName: string): Promise<void> {
    await this.homePageService.loadStateItems();
    // No state name: just load content
    if (!stateName) {
      this.homePageService.selectedState = null;
      await this.loadContent();
      return;
    }

    const stateLower = stateName.toLowerCase();
    let stateFound = false;
    for (const state of this.homePageService.stateItems) {
      // Valid state name: set selected state and load content
      if (stateLower === state.name?.toLowerCase()) {
        stateFound = true;
        this.homePageService.selectedState = state;
        await this.loadContent();
      }
      // Semi valid state name: reroute with valid state name
      if (stateLower === state.readableId?.toLowerCase() || stateLower === state.abbreviation?.toLowerCase()) {
        stateFound = true;
        await this.navigateToState(state);
      }
    }
    if (!stateFound) {
        await this.router.navigate(['/404']);
    }
  }

  private async loadContent() {
    this.loading = true;
    this.sectionObserver.disconnect();
    await this.homePageService.loadMainContent();
    this.loading = false;
    this.cdr.detectChanges();
    this.initObserver();
  }

  public async onSelectedStateChange(stateItem: PickerItem) {
    await this.navigateToState(stateItem);
  }

  private async navigateToState(stateItem: PickerItem) {
    // Just set selected state in web views to avoid messing up navigation
    if (StateService.isWebview) {
      this.homePageService.selectedState = stateItem;
      await this.loadContent();
    } else {
      await this.router.navigate([stateItem?.name?.toLowerCase() || '/']);
    }
  }

  public showRegisterBanner(): boolean {
    return !this.userService.currentUser || this.userService.getLoginType() === LoginType.Guest;
  }

  private initObserver() {
    const sections = [
      { id: 'shop-section', callback: () => this.shopService.init() },
      { id: 'specials-section', callback: () => this.homePageService.loadSpecials() },
      { id: 'past-events-section', callback: () => this.homePageService.loadPastEvents() },
      { id: 'national-leagues-section', callback: () => this.homePageService.loadNationalLeagues() }
    ];

    // Conditional sections based on state selection
    const conditionalSections = this.homePageService.selectedState
      ? [
        { id: 'state-leagues-section', callback: () => this.homePageService.loadStateLeagueItems() },
        { id: 'state-teams-section', callback: () => this.homePageService.loadStateTeamItems() }
      ]
      : [
        { id: 'handball-section', callback: () => this.homePageService.loadHandballVideos() },
        { id: 'favourites-section', callback: () => this.homePageService.loadFavourites() },
        { id: 'international-leagues-section', callback: () => this.homePageService.loadInternationalLeagues() },
        { id: 'states-section', callback: async () => { this.stateSectionIntercepted = true; } }
      ];

    this.sectionObserver.observeElements([
      ...sections,
      ...conditionalSections
    ], true);
  }
}
